(ns client-portal.client.system-configuration.changes.views
  (:require [client-portal.client.system-configuration.fields.views :as fields]
            [clojure.string :as s]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]
            [shared.core.utils.diff :as diff]))

;; Empty lists save as nothing, and multi-line text diffs by line rather than as one escaped string
(defn- side-lines [{:keys [value]}]
  (cond
    (or (nil? value) (and (coll? value) (empty? value))) [:not-set]
    (and (string? value) (s/includes? value "\n")) (s/split-lines value)
    :else (s/split-lines (util/map->json-string value))))

(defn- pair-edits
  "Removed lines replaced by as many added ones are paired up, so each pair can mark just the words that changed"
  [ops]
  (let [ops (vec ops)
        run-of (fn [op from]
                 (vec (take-while #(= op (first %)) (subvec ops from))))
        ->line (fn [[op line]]
                 {:op op
                  :line line})]
    (loop [i 0
           out []]
      (if (>= i (count ops))
        out
        (let [removed (run-of :removed i)
              added (run-of :added (+ i (count removed)))
              n (count removed)]
          (cond
            (and (pos? n) (= n (count added)))
            (recur (+ i n n)
                   (into out (concat (map (fn [[_ a] [_ b]] {:op :removed :line a :pair b}) removed added)
                                     (map (fn [[_ a] [_ b]] {:op :added :line b :pair a}) removed added))))

            (pos? n)
            (recur (+ i n (count added)) (into out (map ->line (concat removed added))))

            :else
            (recur (inc i) (conj out (->line (nth ops i))))))))))

(defn- fold-unchanged
  "Keeps `context` unchanged lines either side of each edit and folds away the rest"
  [lines context]
  (let [runs (partition-by #(= :same (:op %)) lines)
        last-idx (dec (count runs))]
    (if (< (count runs) 2)
      lines
      (->> runs
           (map-indexed (fn [idx run]
                          (let [lead (if (zero? idx) 0 context)
                                tail (if (= idx last-idx) 0 context)
                                hidden (- (count run) lead tail)]
                            (if (and (= :same (:op (first run))) (> hidden 1))
                              (concat (take lead run)
                                      [{:op :fold
                                        :hidden hidden}]
                                      (take-last tail run))
                              run))))
           (apply concat)))))

(defn- marked-text
  "The line with the part that differs from its pair highlighted, when the rest of the line is worth keeping plain"
  [line pair highlight]
  (let [[prefix suffix] (when (and (string? line) (string? pair))
                          (diff/common-affixes line pair))
        end (- (count line) (or suffix 0))
        head (subs line 0 (or prefix 0))
        tail (subs line end)]
    (if (or (nil? prefix) (s/blank? (str head tail)) (>= prefix end))
      line
      [:<>
       head
       [:span {:style {:background-color highlight
                       :border-radius 3
                       :font-family :inherit}}
        (subs line prefix end)]
       tail])))

(defn- diff-line [{:keys [op line pair note]}]
  (let [{:keys [hex sign-color sign label]} (case op
                                              :removed {:hex (style/alc-color "Background/error_const")
                                                        :sign-color (style/alc-color "Common/error")
                                                        :sign "−"
                                                        :label "Removed: "}
                                              :added {:hex (style/alc-color "Common/success_hex")
                                                      :sign-color (style/alc-color "Common/success")
                                                      :sign "+"
                                                      :label "Added: "}
                                              nil)]
    [:div {:style {:background-color (some-> hex (util/hex->rgba 0.14))
                   :display :grid
                   :grid-template-columns "32px minmax(0, 1fr) auto"
                   :column-gap 8
                   :padding "1px 12px 1px 0"}}
     [:span {:aria-hidden true
             :style {:color sign-color
                     :font-family "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"
                     :font-size 12
                     :line-height "20px"
                     :text-align :center
                     :user-select :none}}
      sign]
     [:span {:style {:min-width 0}}
      (when label
        [:span {:class-name "sr-only"}
         label])
      (if (= line :not-set)
        [:span {:style {:color (style/alc-color "Text/secondary")
                        :font-size 13
                        :line-height "20px"}}
         "Not set"]
        [:span {:style {:color (style/alc-color "Text/primary")
                        :font-family "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"
                        :font-size 12
                        :line-height "20px"
                        :overflow-wrap :anywhere
                        :tab-size 2
                        :white-space :pre-wrap}}
         (marked-text line pair (some-> hex (util/hex->rgba 0.32)))])]
     [:span {:style {:color (style/alc-color "Text/secondary")
                     :font-size 12
                     :line-height "20px"}}
      note]]))

(defn- fold-row [{:keys [hidden on-click]}]
  [:button {:type "button"
            :class-name (str "w-full text-left outline-none transition-colors hover:bg-up-background-hover "
                             "focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50")
            :style {:background :none
                    :border :none
                    :color (style/alc-color "Text/secondary")
                    :cursor :pointer
                    :font-size 12
                    :line-height "20px"
                    :padding "2px 12px 2px 40px"}
            :on-click on-click}
   (str "Show " hidden (if (= hidden 1) " Unchanged Line" " Unchanged Lines"))])

(defn- hunk-header [{:keys [path kind]}]
  [:div {:style {:align-items :baseline
                 :background-color (style/alc-color "Background/secondary")
                 :border-bottom (str "1px solid " (style/alc-color "Border/divider_secondary"))
                 :display :flex
                 :gap 12
                 :justify-content :space-between
                 :padding "9px 12px"}}
   [:span {:style {:font-size 13
                   :line-height 1.45
                   :min-width 0}}
    (for [segment (butlast path)]
      ^{:key segment}
      [:span {:style {:color (style/alc-color "Text/secondary")}}
       (str (fields/field-label segment) " › ")])
    [:span {:style {:color (style/alc-color "Text/primary_header")
                    :font-weight 500}}
     (fields/field-label (last path))]]
   [:span {:style {:color (style/alc-color "Text/secondary")
                   :flex-shrink 0
                   :font-size 12
                   :line-height 1.45}}
    (get {:changed "Changed"
          :overridden "Overridden here"
          :inherited "Now inherited"
          :added "Added"
          :cleared "Cleared"
          :turned-off "Turned off here"
          :turned-on "Turned back on"}
         kind)]])

(defn- hunk [_]
  (let [expanded?* (re/ratom false)]
    (fn [{:keys [kind before after] :as change}]
      [:div
       [hunk-header change]
       (if (contains? #{:turned-off :turned-on} kind)
         [:div {:style {:color (style/alc-color "Text/secondary")
                        :font-size 13
                        :line-height 1.45
                        :padding "8px 12px 10px 40px"}}
          (if (= kind :turned-off)
            "Nothing in this group is inherited."
            "This group inherits again.")]
         (let [lines (pair-edits (diff/line-diff (side-lines before) (side-lines after)))
               first-of #(some (fn [[idx {:keys [op]}]] (when (= op %) idx)) (map-indexed vector lines))
               notes (cond-> {}
                             (:inherited? before) (assoc (first-of :removed) "inherited")
                             (:inherited? after) (assoc (first-of :added) "inherited"))
               lines (map-indexed #(assoc %2 :note (get notes %1)) lines)]
           (into [:div {:style {:padding "4px 0"}}]
                 (map (fn [{:keys [op] :as line}]
                        (if (= op :fold)
                          [fold-row {:hidden (:hidden line)
                                     :on-click #(reset! expanded?* true)}]
                          [diff-line line]))
                      (cond-> lines
                              (not @expanded?*) (fold-unchanged 2))))))])))

(defn diff-view [changes]
  (if (empty? changes)
    [:span {:style {:color (style/alc-color "Text/secondary")
                    :font-size 14
                    :line-height 1.43}}
     "Nothing has changed since the last save."]
    (into [:div {:style {:border (str "1px solid " (style/alc-color "Border/divider"))
                         :border-radius 8
                         :overflow :hidden}}]
          (map-indexed (fn [idx change]
                         ^{:key (pr-str (:path change))}
                         [:div {:style {:border-top (when (pos? idx)
                                                      (str "1px solid " (style/alc-color "Border/divider")))}}
                          [hunk change]])
                       changes))))
